import { useEffect, useState } from "react";
import { useOutletContext, useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { endpoints, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, FileText, CalendarIcon } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { formatDateLong, currencyForCountry, SUPPORTED_CURRENCIES } from "@/lib/format";

// Timezone-safe parse: `YYYY-MM-DD` → Date at local midnight (no UTC drift).
function parseISODate(s) {
    if (!s) return null;
    const [y, m, d] = s.split("-").map((n) => parseInt(n, 10));
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
}
function toISODate(date) {
    if (!date) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

export default function VenueEventEditor() {
    const { venue, role } = useOutletContext();
    const { eid } = useParams();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const isEdit = !!eid;
    const [artists, setArtists] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [templateId, setTemplateId] = useState(params.get("template") || "");
    const [saving, setSaving] = useState(false);
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    // access modes are MUTUALLY EXCLUSIVE — represented as a single value
    // 'free' | 'pay_at_door'. Derived is_free / is_pay_at_door on submit.
    const [form, setForm] = useState({
        name: "", date: "", start_time: "20:00", end_time: "23:00",
        capacity: 100, description: "", access: "free",
        ticket_price: "", currency: "", currency_touched: false,
        image_url: "", artist_id: "",
    });

    const applyTemplate = (t) => {
        setForm((f) => ({
            ...f,
            name: t.name || f.name,
            start_time: t.start_time || f.start_time,
            end_time: t.end_time || f.end_time,
            capacity: t.capacity || f.capacity,
            description: t.description || f.description,
            access: t.is_pay_at_door ? "pay_at_door" : "free",
            ticket_price: t.ticket_price ?? "",
            // NEVER pre-fill: date, artist_id, image_url
        }));
        setTemplateId(t.id);
        toast.success(`Loaded template: ${t.name}`);
    };

    useEffect(() => {
        endpoints.publicArtists ? null : null;
        // Load all artists (public list) — reuse existing /api/discover or list_artists endpoint
        import("axios").then(({ default: axios }) => {
            axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/artists`).then((r) => {
                setArtists(r.data?.artists || []);
            }).catch(() => setArtists([]));
        });
        // Load available templates for this venue (create-only flow)
        if (!isEdit) {
            endpoints.venueTemplates(venue.id).then((d) => {
                const list = (d?.templates || []).filter((t) => !t.archived);
                setTemplates(list);
                const preId = params.get("template");
                if (preId) {
                    const preT = list.find((t) => t.id === preId);
                    if (preT) applyTemplate(preT);
                }
            }).catch(() => setTemplates([]));
        }
    }, [isEdit, venue.id]);

    useEffect(() => {
        if (!isEdit) return;
        endpoints.venueEvents(venue.id).then((d) => {
            const ev = (d.all || []).find((e) => e.id === eid);
            if (!ev) { toast.error("Event not found"); navigate(`/venue/${venue.id}/events`); return; }
            setForm({
                name: ev.name || "",
                date: (ev.date || "").slice(0, 10),
                start_time: ev.start_time || "20:00",
                end_time: ev.end_time || "23:00",
                capacity: ev.capacity || 100,
                description: ev.description || "",
                access: ev.is_pay_at_door ? "pay_at_door" : "free",
                ticket_price: ev.ticket_categories?.[0]?.price || "",
                currency: ev.currency || "",
                currency_touched: true,  // existing event — currency is authoritative
                image_url: ev.image_url || "",
                artist_id: ev.artist_id || "",
            });
        });
    }, [isEdit, eid, venue.id, navigate]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    // Currency default: venue-country → override when user manually picks.
    // We never silently assume USD.
    const suggestedCurrency = currencyForCountry(venue?.country) || "";
    const effectiveCurrency = form.currency || (form.currency_touched ? "" : suggestedCurrency);
    const requiresCurrency = form.access !== "free";

    const save = async () => {
        if (!form.name.trim() || !form.date) { toast.error("Name and date required"); return; }
        if (requiresCurrency && !effectiveCurrency) {
            toast.error("Choose a currency (venue country default is missing).");
            return;
        }
        setSaving(true);
        const isFree = form.access === "free";
        const payload = {
            name: form.name.trim(),
            date: form.date,
            start_time: form.start_time,
            end_time: form.end_time,
            capacity: parseInt(form.capacity || 100),
            description: form.description,
            is_free: isFree,
            is_pay_at_door: form.access === "pay_at_door",
            ticket_price: isFree ? null : parseFloat(form.ticket_price || 0),
            currency: isFree ? null : effectiveCurrency,
            image_url: form.image_url,
            artist_id: form.artist_id || null,
            template_id: !isEdit && templateId ? templateId : null,
        };
        try {
            if (isEdit) {
                await endpoints.venueUpdateEvent(venue.id, eid, payload);
                toast.success("Saved");
            } else {
                const r = await endpoints.venueCreateEvent(venue.id, payload);
                toast.success(r.event.status === "pending_approval" ? "Event created — awaiting artist approval" : "Draft created");
            }
            navigate(`/venue/${venue.id}/events`);
        } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
    };

    return (
        <div className="pm-fade" data-testid="venue-event-editor">
            <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-6 flex items-center justify-between flex-wrap gap-3">
                <div>
                    <Link to={`/venue/${venue.id}/events`} className="text-[10px] font-mono uppercase text-[#a3a3a3] flex items-center gap-1 hover:text-[#ff4400]"><ArrowLeft className="w-3 h-3" /> Back to events</Link>
                    <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl mt-1">{isEdit ? "Edit event" : "New event"}</h1>
                </div>
                <button onClick={save} disabled={saving} className="pm-btn pm-btn-primary" data-testid="venue-event-save">
                    <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save"}
                </button>
            </header>

            <div className="px-4 md:px-8 py-6 max-w-3xl space-y-4">
                {!isEdit && templates.length > 0 && (
                    <div className="border border-[#2a2a2a] p-4 bg-[#0a0a0a] flex flex-col md:flex-row md:items-end gap-3" data-testid="venue-event-template-picker">
                        <div className="flex-1">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400] flex items-center gap-1"><FileText className="w-3 h-3" /> Start from template</div>
                            <div className="mt-1 text-sm text-[#a3a3a3]">Pre-fill the form using a saved event setup. Edit any value before saving.</div>
                        </div>
                        <select
                            value={templateId}
                            onChange={(e) => {
                                const id = e.target.value;
                                setTemplateId(id);
                                if (!id) return;
                                const t = templates.find((x) => x.id === id);
                                if (t) applyTemplate(t);
                            }}
                            className="bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none font-mono text-sm min-w-[240px]"
                            data-testid="venue-event-template-select"
                        >
                            <option value="" className="bg-[#141414]">— Choose a template —</option>
                            {templates.map((t) => (
                                <option key={t.id} value={t.id} className="bg-[#141414]">{t.name}</option>
                            ))}
                        </select>
                    </div>
                )}
                <Field label="Event name">
                    <input value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none" data-testid="venue-event-name" />
                </Field>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Field label="Date">
                        <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                            <PopoverTrigger asChild>
                                <button
                                    type="button"
                                    className="w-full flex items-center justify-between bg-transparent border border-[#2a2a2a] px-3 py-2 text-left outline-none hover:border-[#f4f4f0]"
                                    data-testid="venue-event-date-trigger"
                                >
                                    <span className={form.date ? "" : "text-[#666]"}>
                                        {form.date ? formatDateLong(form.date) : "Select a date"}
                                    </span>
                                    <CalendarIcon className="w-4 h-4 text-[#a3a3a3]" />
                                </button>
                            </PopoverTrigger>
                            <PopoverContent
                                className="w-auto p-0 bg-[#141414] border border-[#2a2a2a]"
                                data-testid="venue-event-date-popover"
                                align="start"
                            >
                                <Calendar
                                    mode="single"
                                    selected={parseISODate(form.date) || undefined}
                                    onSelect={(d) => {
                                        if (d) {
                                            set("date", toISODate(d));
                                            setDatePickerOpen(false);
                                        }
                                    }}
                                />
                            </PopoverContent>
                        </Popover>
                    </Field>
                    <Field label="Start time">
                        <input type="time" value={form.start_time} onChange={(e) => set("start_time", e.target.value)} className="w-full bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none" data-testid="venue-event-start" />
                    </Field>
                    <Field label="End time">
                        <input type="time" value={form.end_time} onChange={(e) => set("end_time", e.target.value)} className="w-full bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none" data-testid="venue-event-end" />
                    </Field>
                </div>
                <Field label="Capacity">
                    <input type="number" value={form.capacity} onChange={(e) => set("capacity", e.target.value)} className="w-full bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none" data-testid="venue-event-capacity" />
                </Field>
                <Field label="Artist (optional — leave blank for TBA)">
                    <select value={form.artist_id} onChange={(e) => set("artist_id", e.target.value)} className="w-full bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none font-mono text-sm" data-testid="venue-event-artist">
                        <option value="" className="bg-[#141414]">TBA / Unassigned</option>
                        {artists.map((a) => <option key={a.id} value={a.id} className="bg-[#141414]">{a.name}</option>)}
                    </select>
                    {form.artist_id && !isEdit && (
                        <div className="mt-2 text-[10px] font-mono uppercase text-[#ff9500]">Event will require artist acceptance before it can go public.</div>
                    )}
                </Field>
                <Field label="Description">
                    <textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} className="w-full bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none" data-testid="venue-event-desc" />
                </Field>
                <Field label="Cover image">
                    <ImageUploader value={form.image_url} onChange={(v) => set("image_url", v)} scope="event" artistId={form.artist_id || undefined} aspect="video" testId="venue-event-image" />
                </Field>
                <Field label="Access type">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="venue-event-access-group" role="radiogroup">
                        {[
                            { id: "free", label: "Free entry" },
                            { id: "pay_at_door", label: "Pay at door" },
                        ].map((opt) => (
                            <label
                                key={opt.id}
                                className={`flex items-center gap-2 border px-3 py-2 cursor-pointer ${form.access === opt.id ? "border-[#ff4400] bg-[#141414]" : "border-[#2a2a2a] hover:border-[#f4f4f0]"}`}
                                data-testid={`venue-event-access-${opt.id}`}
                            >
                                <input
                                    type="radio"
                                    name="venue-event-access"
                                    checked={form.access === opt.id}
                                    onChange={() => set("access", opt.id)}
                                    className="accent-[#ff4400]"
                                />
                                <span className="text-xs font-mono uppercase">{opt.label}</span>
                            </label>
                        ))}
                    </div>
                </Field>
                {form.access === "pay_at_door" && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field label="Display price">
                            <input type="number" step="0.01" value={form.ticket_price} onChange={(e) => set("ticket_price", e.target.value)} className="w-full bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none" data-testid="venue-event-price" />
                        </Field>
                        <Field label={`Currency ${suggestedCurrency && !form.currency_touched ? `(default ${suggestedCurrency})` : ""}`}>
                            <select
                                value={effectiveCurrency}
                                onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value, currency_touched: true }))}
                                className="w-full bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none font-mono text-sm"
                                data-testid="venue-event-currency"
                            >
                                <option value="" className="bg-[#141414]">Select currency…</option>
                                {SUPPORTED_CURRENCIES.map((c) => (
                                    <option key={c.code} value={c.code} className="bg-[#141414]">
                                        {c.code} — {c.label}
                                    </option>
                                ))}
                            </select>
                        </Field>
                    </div>
                )}
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#666] mb-1">{label}</div>
            {children}
        </label>
    );
}
